import { TextField, Box, Button, Paper, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { getProductByIdThunk, getProductsThunk, updateProductThunk } from '@redux/features/product.slice';
import { getCategoriesThunk, getCategoryWithSubByIdThunk } from '@redux/features/category.slice';
import { getDosagesThunk } from '@redux/features/dosage.slice';
import { getManufacturersThunk } from '@redux/features/manufacturer.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC, useMemo, useEffect, useState, useRef } from 'react';
import * as Yup from 'yup';

interface IProps {
    show: boolean;
    handleClose: () => void;
    productId: number;
}

const UpdateProductModal: FC<IProps> = ({ show, handleClose, productId }) => {
    const { updateLoading, product, productLoading } = useAppSelector((state: RootState) => state.productState);
    const { categories } = useAppSelector((state: RootState) => state.categoryState);
    const { dosages } = useAppSelector((state: RootState) => state.dosageState);
    const { manufacturers } = useAppSelector((state: RootState) => state.manufacturerState);
    const dispatch = useAppDispatch();
    const [selectedSubCategories, setSelectedSubCategories] = useState<any[]>([]);
    const isInitialLoadRef = useRef(false);

    // Fetch dropdown data when modal opens
    useEffect(() => {
        if (show) {
            dispatch(getCategoriesThunk());
            dispatch(getDosagesThunk());
            dispatch(getManufacturersThunk());
        } else {
            // Reset subcategories when modal closes
            setSelectedSubCategories([]);
            isInitialLoadRef.current = false;
        }
    }, [show, dispatch]);

    const validationSchema = Yup.object({
        itemName: Yup.string().required('Product name is required'),
        unitPrice: Yup.number().required('Unit price is required').min(0, 'Must be >= 0'),
        priceOffer: Yup.number().required('Price offer is required').min(0, 'Must be >= 0'),
        brandName: Yup.string().required('Brand name is required'),
    });

    const handleSubmit = (values: any) => {
        const body: any = {
            id: productId,
            itemName: values.itemName,
            itemReference: values.itemReference,
            unitPrice: Number(values.unitPrice),
            priceOffer: Number(values.priceOffer),
            brandName: values.brandName,
            brandGeneric: values.brandGeneric,
            ndcCode: values.ndcCode,
            barcode: values.barcode,
            strength: values.strength,
            packSize: values.packSize,
            companyPrefix: values.companyPrefix,
            packagingUom: values.packagingUom,
            wholesaleUom: values.wholesaleUom,
            storageCondition: values.storageCondition,
            locationInWarehouse: values.locationInWarehouse,
            additionalInfo: values.additionalInfo,
            reorderLevel: Number(values.reorderLevel || 0),
            stockLevel: Number(values.stockLevel || 0),
            categoryId: Number(values.categoryId || 0),
            subCategoriesId: Number(values.subCategoriesId || 0),
            dosageId: Number(values.dosageId || 0),
            manufacturersId: Number(values.manufacturersId || 0),
            gtin: values.gtin,
            photoFile: values.photoFile,
        };
        dispatch(updateProductThunk(body)).then((res: any) => {
            if (res.meta.requestStatus === 'fulfilled') {
                dispatch(getProductsThunk({ page: 0, productSize: 100 }));
                handleClose();
                formik.resetForm();
            }
        });
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            itemReference: '',
            itemName: '',
            unitPrice: 0,
            priceOffer: 0,
            brandName: '',
            brandGeneric: '',
            ndcCode: '',
            barcode: '',
            strength: '',
            packSize: '',
            companyPrefix: '',
            packagingUom: '',
            wholesaleUom: '',
            storageCondition: '',
            locationInWarehouse: '',
            additionalInfo: '',
            reorderLevel: 0,
            stockLevel: 0,
            categoryId: 0,
            subCategoriesId: 0,
            dosageId: 0,
            manufacturersId: 0,
            gtin: '',
            photoFile: undefined as unknown as File,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    const previews = useMemo(() => {
        const toUrl = (f?: File) => (f && f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
        return {
            photoFile: toUrl(formik.values.photoFile),
        } as Record<string, string | null>;
    }, [formik.values.photoFile]);

    // Fetch product data when modal opens
    useEffect(() => {
        if (show && productId) {
            dispatch(getProductByIdThunk(productId));
        }
    }, [show, productId, dispatch]);

    // Populate form when product data is loaded
    useEffect(() => {
        if (product && show && product.id === productId && !productLoading) {
            isInitialLoadRef.current = true;

            // Get categoryId - check both direct property and nested subCategories
            const categoryId = product.categoryId ?? (product as any).subCategories?.categoryId ?? 0;
            // Get subCategoriesId - check both direct property and nested subCategories
            const subCategoriesId = product.subCategoriesId ?? (product as any).subCategories?.id ?? 0;

            formik.setValues({
                itemReference: product.itemReference ?? '',
                itemName: product.itemName ?? '',
                unitPrice: product.unitPrice ?? 0,
                priceOffer: product.priceOffer ?? 0,
                brandName: product.brandName ?? '',
                brandGeneric: product.brandGeneric ?? '',
                ndcCode: product.ndcCode ?? '',
                barcode: product.barcode ?? '',
                strength: product.strength ?? '',
                packSize: product.packSize ?? '',
                companyPrefix: product.companyPrefix ?? '',
                packagingUom: product.packagingUom ?? '',
                wholesaleUom: product.wholesaleUom ?? '',
                storageCondition: product.storageCondition ?? '',
                locationInWarehouse: product.locationInWarehouse ?? '',
                additionalInfo: product.additionalInfo ?? '',
                reorderLevel: product.reorderLevel ?? 0,
                stockLevel: product.stockLevel ?? 0,
                categoryId: categoryId,
                subCategoriesId: subCategoriesId,
                dosageId: (product as any).dosage?.id ?? 0,
                manufacturersId: (product as any).manufacturers?.[0]?.id ?? 0,
                gtin: product.gtin ?? '',
                photoFile: undefined as unknown as File,
            });

            // Fetch subcategories if category is selected
            if (categoryId && categoryId !== 0) {
                dispatch(getCategoryWithSubByIdThunk(categoryId)).then((res: any) => {
                    if (res.meta.requestStatus === 'fulfilled' && res.payload?.Category?.subCategories) {
                        setSelectedSubCategories(res.payload.Category.subCategories);
                    } else {
                        setSelectedSubCategories([]);
                    }
                    isInitialLoadRef.current = false;
                });
            } else {
                setSelectedSubCategories([]);
                isInitialLoadRef.current = false;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product, show, productId, productLoading, dispatch]);

    // Fetch subcategories when category is selected (only when user changes category, not on initial load)
    useEffect(() => {
        // Skip if this is the initial load
        if (isInitialLoadRef.current) {
            return;
        }

        const categoryId = formik.values.categoryId;
        if (categoryId && categoryId !== 0) {
            dispatch(getCategoryWithSubByIdThunk(categoryId)).then((res: any) => {
                if (res.meta.requestStatus === 'fulfilled' && res.payload?.Category?.subCategories) {
                    setSelectedSubCategories(res.payload.Category.subCategories);
                } else {
                    setSelectedSubCategories([]);
                }
            });
            // Reset subcategory when category changes (user action)
            formik.setFieldValue('subCategoriesId', 0);
        } else {
            setSelectedSubCategories([]);
            formik.setFieldValue('subCategoriesId', 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.categoryId, dispatch]);

    return (
        <Modal
            show={show}
            onHide={() => handleClose()}
            dialogClassName="w-full bg-white shadow rounded-md overflow-hidden md:w-[1000px] md:h-[90vh] flex flex-col"
        >
            <Modal.Header className="p-3 flex justify-between items-center">
                <Modal.Title className="text-lg font-semibold">Update Product</Modal.Title>
            </Modal.Header>
            <Modal.Body className="flex-1 overflow-y-auto p-4">
                {productLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Typography variant="body1">Loading product data...</Typography>
                    </div>
                ) : (
                    <form onSubmit={formik.handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            id="itemName"
                            name="itemName"
                            label="Product Name"
                            value={formik.values.itemName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.itemName && Boolean(formik.errors.itemName)}
                            helperText={formik.touched.itemName && formik.errors.itemName}
                        />
                        <TextField
                            disabled={true}
                            size="small"
                            fullWidth
                            id="itemReference"
                            name="itemReference"
                            label="Item Reference (Read-only, managed by EPCIS)"
                            value={formik.values.itemReference}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.itemReference && Boolean(formik.errors.itemReference)}
                            helperText={formik.touched.itemReference && formik.errors.itemReference}
                        />
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            type="number"
                            id="unitPrice"
                            name="unitPrice"
                            label="Unit Price"
                            value={formik.values.unitPrice}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.unitPrice && Boolean(formik.errors.unitPrice)}
                            helperText={formik.touched.unitPrice && formik.errors.unitPrice}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            type="number"
                            id="priceOffer"
                            name="priceOffer"
                            label="Price Offer"
                            value={formik.values.priceOffer}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.priceOffer && Boolean(formik.errors.priceOffer)}
                            helperText={formik.touched.priceOffer && formik.errors.priceOffer}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            id="brandName"
                            name="brandName"
                            label="Brand Name"
                            value={formik.values.brandName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.brandName && Boolean(formik.errors.brandName)}
                            helperText={formik.touched.brandName && formik.errors.brandName}
                        />

                        <FormControl fullWidth size="small">
                            <InputLabel id="brandGeneric-label">Brand Generic</InputLabel>
                            <Select
                                disabled={updateLoading || productLoading}
                                labelId="brandGeneric-label"
                                id="brandGeneric"
                                name="brandGeneric"
                                label="Brand Generic"
                                value={formik.values.brandGeneric || ''}
                                onChange={(e) => formik.setFieldValue('brandGeneric', e.target.value)}
                            >
                                <MenuItem value="Brand">Brand</MenuItem>
                                <MenuItem value="Generic">Generic</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            id="ndcCode"
                            name="ndcCode"
                            label="NDC Code"
                            value={formik.values.ndcCode}
                            onChange={formik.handleChange}
                        />
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            id="barcode"
                            name="barcode"
                            label="Barcode"
                            value={formik.values.barcode}
                            onChange={formik.handleChange}
                        />
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            id="strength"
                            name="strength"
                            label="Strength"
                            value={formik.values.strength}
                            onChange={formik.handleChange}
                        />
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            id="packSize"
                            name="packSize"
                            label="Pack Size"
                            value={formik.values.packSize}
                            onChange={formik.handleChange}
                        />
                        <TextField
                            disabled={true}
                            size="small"
                            fullWidth
                            id="companyPrefix"
                            name="companyPrefix"
                            label="Company Prefix (Read-only, managed by EPCIS)"
                            value={formik.values.companyPrefix}
                            onChange={formik.handleChange}
                        />
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            id="packagingUom"
                            name="packagingUom"
                            label="Packaging UOM"
                            value={formik.values.packagingUom}
                            onChange={formik.handleChange}
                        />
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            id="wholesaleUom"
                            name="wholesaleUom"
                            label="Wholesale UOM"
                            value={formik.values.wholesaleUom}
                            onChange={formik.handleChange}
                        />
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            id="storageCondition"
                            name="storageCondition"
                            label="Storage Condition"
                            value={formik.values.storageCondition}
                            onChange={formik.handleChange}
                        />
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            id="locationInWarehouse"
                            name="locationInWarehouse"
                            label="Location in Warehouse"
                            value={formik.values.locationInWarehouse}
                            onChange={formik.handleChange}
                        />
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            id="additionalInfo"
                            name="additionalInfo"
                            label="Additional Info"
                            value={formik.values.additionalInfo}
                            onChange={formik.handleChange}
                        />
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            type="number"
                            id="reorderLevel"
                            name="reorderLevel"
                            label="Reorder Level"
                            value={formik.values.reorderLevel}
                            onChange={formik.handleChange}
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            type="number"
                            id="stockLevel"
                            name="stockLevel"
                            label="Stock Level"
                            value={formik.values.stockLevel}
                            onChange={formik.handleChange}
                            inputProps={{ min: 0 }}
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel id="categoryId-label">Category</InputLabel>
                            <Select
                                disabled={updateLoading || productLoading}
                                labelId="categoryId-label"
                                id="categoryId"
                                name="categoryId"
                                label="Category"
                                value={formik.values.categoryId ?? 0}
                                onChange={(e) => formik.setFieldValue('categoryId', Number(e.target.value))}
                            >
                                <MenuItem value={0}>None</MenuItem>
                                {categories?.map((category: { id: number; categoryName: string }) => (
                                    <MenuItem key={category.id} value={category.id}>
                                        {category.categoryName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel id="subCategoriesId-label">Sub Category</InputLabel>
                            <Select
                                disabled={
                                    updateLoading ||
                                    !formik.values.categoryId ||
                                    formik.values.categoryId === 0 ||
                                    selectedSubCategories.length === 0
                                }
                                labelId="subCategoriesId-label"
                                id="subCategoriesId"
                                name="subCategoriesId"
                                label="Sub Category"
                                value={formik.values.subCategoriesId ?? 0}
                                onChange={(e) => formik.setFieldValue('subCategoriesId', Number(e.target.value))}
                            >
                                <MenuItem value={0}>None</MenuItem>
                                {selectedSubCategories?.map((subCategory: { id: number; categoryName: string }) => (
                                    <MenuItem key={subCategory.id} value={subCategory.id}>
                                        {subCategory.categoryName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel id="dosageId-label">Dosage</InputLabel>
                            <Select
                                disabled={updateLoading || productLoading}
                                labelId="dosageId-label"
                                id="dosageId"
                                name="dosageId"
                                label="Dosage"
                                value={formik.values.dosageId ?? 0}
                                onChange={(e) => formik.setFieldValue('dosageId', Number(e.target.value))}
                            >
                                <MenuItem value={0}>None</MenuItem>
                                {dosages?.map((dosage: { id: number; dosageName: string }) => (
                                    <MenuItem key={dosage.id} value={dosage.id}>
                                        {dosage.dosageName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel id="manufacturersId-label">Manufacturer</InputLabel>
                            <Select
                                disabled={updateLoading || productLoading}
                                labelId="manufacturersId-label"
                                id="manufacturersId"
                                name="manufacturersId"
                                label="Manufacturer"
                                value={formik.values.manufacturersId ?? 0}
                                onChange={(e) => formik.setFieldValue('manufacturersId', Number(e.target.value))}
                            >
                                <MenuItem value={0}>None</MenuItem>
                                {manufacturers?.map((manufacturer: { id: number; name: string }) => (
                                    <MenuItem key={manufacturer.id} value={manufacturer.id}>
                                        {manufacturer.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            disabled={updateLoading || productLoading}
                            size="small"
                            fullWidth
                            id="gtin"
                            name="gtin"
                            label="GTIN"
                            value={formik.values.gtin}
                            onChange={formik.handleChange}
                        />
                        <div className="col-span-full">
                            <Paper variant="outlined" className="p-3 flex items-center gap-3">
                                <Box className="flex-1">
                                    <Typography variant="body2" className="mb-1 text-slate-700">
                                        Photo File
                                    </Typography>
                                    {(formik.values as any).photoFile ? (
                                        <Box className="flex items-center gap-3">
                                            {(previews as any).photoFile ? (
                                                // eslint-disable-next-line @next/next/no-img-element
                                                <img
                                                    src={(previews as any).photoFile}
                                                    alt="preview"
                                                    className="w-12 h-12 rounded object-cover border"
                                                />
                                            ) : (
                                                <Box className="w-12 h-12 rounded border flex items-center justify-center text-xs text-slate-500">
                                                    File
                                                </Box>
                                            )}
                                            <Box className="min-w-0">
                                                <Typography variant="body2" className="truncate max-w-[220px]">
                                                    {((formik.values as any).photoFile as File).name}
                                                </Typography>
                                                <Typography variant="caption" className="text-slate-500">
                                                    {(((formik.values as any).photoFile as File).size / 1024).toFixed(
                                                        1
                                                    )}{' '}
                                                    KB
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : product?.photoFile ? (
                                        <Box className="flex items-center gap-3">
                                            <img
                                                src={
                                                    typeof product.photoFile === 'object'
                                                        ? product.photoFile.path
                                                        : product.photoFile
                                                }
                                                alt="product"
                                                className="w-12 h-12 rounded object-cover border"
                                            />
                                            <Typography variant="caption" className="text-slate-500">
                                                Current photo
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" className="text-slate-500">
                                            No file selected
                                        </Typography>
                                    )}
                                </Box>
                                <Box className="flex items-center gap-2">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        component="label"
                                        disabled={updateLoading || productLoading}
                                    >
                                        Upload
                                        <input
                                            hidden
                                            type="file"
                                            accept="image/*,.pdf,.doc,.docx,.png,.jpg,.jpeg"
                                            onChange={(e) =>
                                                (formik as any).setFieldValue('photoFile', e.currentTarget.files?.[0])
                                            }
                                        />
                                    </Button>
                                    {(formik.values as any).photoFile && (
                                        <Button
                                            variant="text"
                                            size="small"
                                            disabled={updateLoading || productLoading}
                                            onClick={() => (formik as any).setFieldValue('photoFile', undefined)}
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </Box>
                            </Paper>
                        </div>
                    </form>
                )}
            </Modal.Body>
            <Modal.Footer className="sticky bottom-0 left-0 right-0 bg-white border-t px-4 py-3 flex items-center justify-center gap-2">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={() => {
                        handleClose();
                        formik.resetForm();
                    }}
                    disabled={updateLoading || productLoading}
                />
                <ButtonBase
                    innerText="Update Product"
                    className="bg-yellow-600 hover:bg-yellow-800 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={formik.handleSubmit}
                    disabled={updateLoading || productLoading || !formik.values.itemName || formik.values.unitPrice < 0}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default UpdateProductModal;
